/* global window */

import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Dropdown from 'react-bootstrap/Dropdown';
import Avatar from 'react-avatar';
import '../styles/Navigation.css';
import { Link } from 'react-router-dom';
import isURL from 'validator/lib/isURL';
import RxHamburgerMenu from '../icons/RxHamburgerMenu';
import AiOutlineClose from '../icons/AiOutlineClose';
import SearchBox from './SearchBox';
import FreeShippingBlock from './FreeShippingBlock';
import Cart from './Cart';
import AppContext from './context/AppContext';
import { array, bool, number, object, string } from 'prop-types';

/**
 * CONFIGURATION
 */
const VERTICAL_SCROLL_EXPAND = 200;
const VERTICAL_SCROLL_HEIGHT_POINT = 150;
const ANNOUNCEMENT_BAR_HEIGHT = 48.2;
const MAX_CHILDREN = 10;
const DEFAULT_TOP_OFFSET = 0;

const NestedNavigationLink = ({ navLink, isDropdownItem, isMobile, expanded }) => {
    const dropdownRef = useRef(null);
    const dropdownMenuRef = useRef(null);
    const [show, setShow] = useState(false);
    const [visibility, setVisibility] = useState('hidden');
    const [alignLeft, setAlignLeft] = useState(false);
    const [offsetTop, setOffsetTop] = useState(DEFAULT_TOP_OFFSET);
    const onMouseOver = () => {
        setShow(true);
    };
    const onMouseLeave = () => {
        setShow(false);
    };
    useEffect(() => {
        const detectOutsideClick = (e) => {
            if (dropdownRef.current !== null && show === true
                && !dropdownRef.current.contains(e.target)) {
                setShow(false);
            }
        };
        window.addEventListener('click', detectOutsideClick, true);
        // Set visibility to hidden to prevent flickering
        setVisibility('hidden');
        return () => {
            window.removeEventListener('click', detectOutsideClick, true);
        };
    }, [show]);
    useLayoutEffect(() => {
        const timer = setTimeout(() => {
            // Align dropdown to the left if it's off the screen
            const dropdownMenu = dropdownMenuRef.current;
            if (dropdownMenu !== null) {
                const dropdownMenuRect = dropdownMenu.getBoundingClientRect();
                if (dropdownMenuRect.right > window.innerWidth) {
                    setAlignLeft(true);
                }
                // Offset dropdown if it's off the screen
                if (dropdownMenuRect.bottom > window.innerHeight) {
                    setOffsetTop(dropdownMenuRect.bottom - window.innerHeight);
                }
                setVisibility('visible');
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [show]);
    return (<>
        {!navLink.children?.length && (<>
            {isDropdownItem === false && (<Nav.Link
                {...isURL(navLink.url) ? ({ href: navLink.url }) : ({
                    as: Link,
                    to: navLink.url
                })}
                className={'ease-in me-2'}>{navLink.title}</Nav.Link>)}
            {isDropdownItem === true && (<Dropdown.Item
                {...isURL(navLink.url) ? ({ href: navLink.url }) : ({
                    as: Link,
                    to: navLink.url
                })}
                className={'ease-in me-2'}>{navLink.title}</Dropdown.Item >)}
        </>)}
        {navLink.children?.length > 0 && (<>
            <Dropdown
                as={Nav.Item}
                ref={dropdownRef}
                menuVariant={'light'}
                className={`${isDropdownItem === true ? 'nested-dropdown' : ''}${alignLeft === true ? ' left' : ''}`}
                {...(!isMobile) ? ({
                    onMouseOver: onMouseOver,
                    onMouseLeave: onMouseLeave,
                    show: show,
                }) : ({
                    onToggle: (newShowValue) => setShow(newShowValue),
                    show: show,
                })}>
                <Dropdown.Toggle
                    as={Nav.Link}
                    className={'ease-in'}>
                    {navLink.title}
                </Dropdown.Toggle>
                <Dropdown.Menu
                    style={{
                        ...(offsetTop > 0) && ({ top: `-${offsetTop}px` }),
                        visibility: visibility,
                        ...show && ({
                            justifyContent: 'flex-start',
                            alignItems: 'space-between',
                            display: 'flex',
                            flexDirection: 'column',
                        }),
                    }}
                    ref={dropdownMenuRef}>
                    {isMobile === true && (<button
                        style={{
                            background: 'transparent',
                            padding: '0',
                            border: '0'
                        }}
                        className={'m-2'}
                        onClick={() => setShow(false)}><AiOutlineClose
                            size={'25px'} /></button>)}
                    {navLink.url && (<Dropdown.Item
                        {...isURL(navLink.url) ? ({ href: navLink.url }) : ({
                            as: Link,
                            to: navLink.url
                        })}
                        className={'ease-in'}>{navLink.title}</Dropdown.Item>)}
                    {navLink.children.slice(0, MAX_CHILDREN).map((navLinkChild) => <NestedNavigationLink
                        key={navLinkChild.id}
                        navLink={navLinkChild}
                        isDropdownItem={true}
                        isMobile={isMobile}
                        expanded={expanded} />)}
                    {navLink.children.length > MAX_CHILDREN && (<>
                        <NestedNavigationLink
                            navLink={{
                                title: 'See more',
                                children: navLink.children.slice(MAX_CHILDREN),
                            }}
                            isDropdownItem={true}
                            isMobile={isMobile}
                            expanded={expanded}
                        />
                    </>)}
                </Dropdown.Menu>
            </Dropdown>
        </>)}
    </>);
};

NestedNavigationLink.propTypes = {
    navLink: object,
    isDropdownItem: bool,
    isMobile: bool,
    expanded: bool,
};

const Navigation = ({ userFirstName, userLastName, navData, isMobile, scrollY, searchBoxRef }) => {
    const { appContext } = useContext(AppContext);
    const appConfig = appContext.config.themeConfig;
    const navbarRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [searchLoaded, setSearchLoaded] = useState(false);
    const [productList, setProductList] = useState([]);
    const [searchQuery, setSearchQuery] = useState({
        q: '',
        v: '',
        sortBy: '',
        limit: appConfig.defaultLimitPerPage,
        page: 0,
        minPrice: '',
        maxPrice: '',
        t: [],
        c: [],
    });
    const name = (userFirstName || userLastName ? userFirstName + ' ' + userLastName : '');
    const title = (<>
        <Avatar
            size={'50px'}
            name={name}
            round={true}
            className={'me-2'} />
        <span>{name}</span>
    </>);
    const guestTitle = (<>
        <span>Account</span>
    </>);
    const handleSelect = (eventKey) => {
        if (eventKey === '4.1') {
            window.location.assign('/account');
        }
        if (eventKey === '4.4') {
            window.location.assign('/logout');
        }
        if (eventKey === '4.5') {
            window.location.assign('/login');
        }
        if (eventKey === '4.6') {
            window.location.assign('/register');
        }
        if (eventKey === '4.7') {
            window.location.assign('/reset-password');
        }
    };
    useEffect(() => {
        const detectOutsideClick = (e) => {
            if (navbarRef.current !== null && expanded === true
                && !navbarRef.current.contains(e.target)) {
                setExpanded(false);
            }
            if (navbarRef.current !== null && expanded === true
                && navbarRef.current.contains(e.target)) {
                if (e.target.tagName === 'A' && !e.target.classList.contains('dropdown-toggle')) {
                    setExpanded(false);
                }
            }
        };
        window.addEventListener('click', detectOutsideClick, true);
        return () => {
            window.removeEventListener('click', detectOutsideClick, true);
        };
    }, [expanded]);
    return (<Navbar
        ref={navbarRef}
        bg={'white'}
        variant={'light'}
        expand={scrollY < VERTICAL_SCROLL_EXPAND ? 'lg' : false}
        className={`navbar__ab ${(expanded && !isMobile) ? 'desktop-expanded' : ''}`}
        onToggle={(expandedNewValue) => setExpanded(expandedNewValue)}
        expanded={expanded}
        style={{
            position: 'fixed',
            top: ANNOUNCEMENT_BAR_HEIGHT + 'px',
            left: '0',
            zIndex: '10000',
            ...scrollY >= ANNOUNCEMENT_BAR_HEIGHT && ({
                top: '0',
            }),
            ...((scrollY >= VERTICAL_SCROLL_HEIGHT_POINT && !expanded) && !isMobile) && ({
                height: '90px',
            }),
            ...((scrollY < VERTICAL_SCROLL_HEIGHT_POINT || expanded) && !isMobile) && ({
                height: '158px',
            }),
        }}>
        <div
            className={'nav-first-level'}>
            <div
                className={'navigation--first-level-head'}>
                <Navbar.Toggle
                    aria-controls={'basic-navbar-nav'}>
                    {expanded === false && (<RxHamburgerMenu
                        size={'25px'} />)}
                    {expanded === true && (<AiOutlineClose
                        size={'25px'} />)}
                </Navbar.Toggle>
                <Navbar.Brand
                    href={'/'}>
                    <img
                        src={appContext.config.storeLogoUrl}
                        width={'150px'}
                        className={'d-inline-block align-top'}
                        style={{
                            transition: '0.4s width ease-in-out',
                            maxHeight: '60px',
                            minHeight: '32px',
                            objectFit: 'contain',
                        }}
                        alt={appContext.config.storeName}
                    />
                </Navbar.Brand>
                {/* Desktop cart block */}
                {isMobile === true && (<Cart
                    isMobile={true} />)}
            </div>
            {/* Free shipping code block */}
            <SearchBox
                isMobile={isMobile}
                searchLoaded={searchLoaded}
                setSearchLoaded={setSearchLoaded}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                productList={productList}
                ref={searchBoxRef}
                setProductList={setProductList} />
            <div
                className={'navigation--first-level-tail'}>
                <FreeShippingBlock
                    heading={'Fast & Free Shipping'}
                    text={'on all orders over ' + appConfig.freeShippingAmount}
                    isMobile={isMobile} />
                {/* Mobile cart block */}
                {isMobile === false && (<Cart
                    isMobile={false} />)}
            </div>
        </div>
        <Container
            className={'nav-second-level'}>
            <Navbar.Collapse
                id={'basic-navbar-nav'}>
                <Nav
                    className={'me-auto'}>
                    {navData.map((navLink) => <NestedNavigationLink
                        key={navLink.id}
                        navLink={navLink}
                        isDropdownItem={false}
                        isMobile={isMobile}
                        expanded={expanded} />)}
                </Nav>
                <Nav
                    variant={'light'}>
                    <Dropdown
                        as={Nav.Item}
                        onSelect={handleSelect}
                        menuVariant={'light'}
                        id={'nav-dropdown-user'}>
                        <Dropdown.Toggle
                            as={Nav.Link}>{name !== '' ? title : guestTitle}</Dropdown.Toggle>
                        <Dropdown.Menu>
                            {name !== '' && (<>
                                <Dropdown.Item
                                    eventKey={'4.1'}>My Account</Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                    eventKey={'4.4'}>Logout</Dropdown.Item>
                            </>)}
                            {name === '' && (<>
                                <Dropdown.Item
                                    eventKey={'4.5'}>Login</Dropdown.Item>
                                <Dropdown.Item
                                    eventKey={'4.6'}>Register</Dropdown.Item>
                                <Dropdown.Item
                                    eventKey={'4.7'}>Recover Account</Dropdown.Item>
                            </>)}
                        </Dropdown.Menu>
                    </Dropdown>
                </Nav>
            </Navbar.Collapse>
        </Container>
    </Navbar>);
};

Navigation.propTypes = {
    userFirstName: string,
    userLastName: string,
    navData: array,
    isMobile: bool,
    scrollY: number,
    searchBoxRef: object,
};

export default Navigation;